// src/components/Engagement/Events.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { getInitials, getRandomColor, shouldShowApiToast } from '../../utils/helpers';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getEvents,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  duplicateEvent,
  sendEventInvites,
  sendEventReminders,
  completeEvent,
  getEventRsvps,
  submitEventRsvp,
  getEventStats,
  exportEvents,
  clearApiMessage,
  clearError,
  clearEventState,
  clearCurrentEvent,
  clearRsvps,
  selectEvents,
  selectCurrentEvent,
  selectEventRsvps,
  selectEventStats,
  selectEventPagination,
  selectEventLoading,
  selectEventError,
  selectEventApiMessage,
  selectEventSuccess,
  selectEventExportData,
  selectEventStatsSummary,
} from '../../store/slices/engagement/eventSlice';
import { 
  getMembers, 
  selectMembers,
  selectMemberPagination,
  selectAvailableGroups 
} from '../../store/slices/main/memberSlice';
import { 
  getVisitors, 
  selectVisitors,
  selectVisitorPagination 
} from '../../store/slices/growth/visitorSlice';
import { selectCurrentOrganization } from '../../store/slices/authSlice';
import { getMergeFieldValue } from '../../utils/mergeFieldValues';
import { Download, BarChart3, ClipboardList, FileSpreadsheet, File, Calendar, Pencil, Trash2, Save, AlertTriangle, Lightbulb, Loader2, Search, Send, Bell, CheckCircle2, XCircle, Eye, Copy, MapPin, Users, LayoutGrid, List, UserPlus, Users2 } from 'lucide-react';

// ==================== HELPER: Format Date & Time ====================
const formatDateTime = (dateString, timeString) => {
  if (!dateString && !timeString) return 'N/A';
  
  let formattedDate = '';
  if (dateString) {
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        formattedDate = months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
      }
    } catch (e) {
      formattedDate = dateString;
    }
  }
  
  let formattedTime = '';
  if (timeString) {
    try {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        let hours = parseInt(parts[0]);
        const minutes = parts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        formattedTime = hours + ':' + minutes + ' ' + ampm;
      } else {
        formattedTime = timeString;
      }
    } catch (e) {
      formattedTime = timeString;
    }
  }
  
  if (formattedDate && formattedTime) {
    return formattedDate + ' · ' + formattedTime;
  } else if (formattedDate) {
    return formattedDate;
  } else if (formattedTime) {
    return formattedTime;
  }
  return 'N/A';
};

const Events = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dynamicAudienceOptions, setDynamicAudienceOptions] = useState([]);
  const [mergeFields] = useState([
    { key: '{ChurchName}', label: 'Church Name', description: 'Your church name', example: 'Grace Chapel' },
    { key: '{EventTitle}', label: 'Event Title', description: 'The event title', example: 'Easter Sunday' },
    { key: '{Date}', label: 'Date', description: 'Event date', example: 'Apr 20, 2024' },
    { key: '{Time}', label: 'Time', description: 'Event time', example: '10:00 AM' },
    { key: '{Venue}', label: 'Venue', description: 'Event venue', example: 'Main Auditorium' },
    { key: '{FirstName}', label: 'First Name', description: "Member's first name", example: 'John' },
    { key: '{LastName}', label: 'Last Name', description: "Member's last name", example: 'Doe' },
    { key: '{FullName}', label: 'Full Name', description: "Member's full name", example: 'John Doe' },
  ]);

  // Refs for edit form
  const editTitleRef = useRef(null);
  const editDateRef = useRef(null);
  const editTimeRef = useRef(null);
  const editVenueRef = useRef(null);
  const editInviteRef = useRef(null);
  const editMessageRef = useRef(null);
  const editReminderRef = useRef(null);

  // Refs for create form
  const createTitleRef = useRef(null);
  const createDateRef = useRef(null);
  const createTimeRef = useRef(null);
  const createVenueRef = useRef(null);
  const createInviteRef = useRef(null);
  const createMessageRef = useRef(null);
  const createReminderRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const events = useSelector(selectEvents) || [];
  const currentEvent = useSelector(selectCurrentEvent);
  const rsvps = useSelector(selectEventRsvps) || [];
  const stats = useSelector(selectEventStatsSummary);
  const pagination = useSelector(selectEventPagination);
  const loading = useSelector(selectEventLoading);
  const apiMessage = useSelector(selectEventApiMessage);
  const error = useSelector(selectEventError);
  const successMessage = useSelector(selectEventSuccess);
  const exportData = useSelector(selectEventExportData);
  
  // MEMBER AND VISITOR SELECTORS
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};
  const groups = useSelector(selectAvailableGroups) || [];
  const organization = useSelector(selectCurrentOrganization);
  
  const resolvedMergeFields = mergeFields.map((field) => ({
    ...field,
    example: getMergeFieldValue(field.key, {
      member: members[0],
      organization,
      event: currentEvent || events[0]
    })
  }));

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingOptions(true);
      try {
        await Promise.all([
          dispatch(getEvents({ 
            per_page: itemsPerPage, 
            page: currentPage,
            search: searchTerm,
            status: statusFilter
          })),
          dispatch(getEventStats()),
          dispatch(getMembers({ per_page: 50 })),
          dispatch(getVisitors({ per_page: 50 })),
        ]);
      } catch (err) {
        console.error('Error loading events:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearEventState());
    };
  }, [dispatch]);

  // Build dynamic audience options
  useEffect(() => {
    buildDynamicAudienceOptions();
  }, [members, visitors, groups, memberPagination, visitorPagination]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getEvents(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    const message = apiMessage || successMessage;
    if (!message || !showToast || !shouldShowApiToast(message, !!error)) return;
    
    const messageKey = `${message}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(message, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, successMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== BUILD DYNAMIC AUDIENCE OPTIONS ====================
  
  const buildDynamicAudienceOptions = () => {
    const options = [];
    
    // 1. MEMBER OPTIONS
    const memberCount = memberPagination?.total || members.length || 0;
    const activeMemberCount = members.filter(m => m.status === 'Active').length;
    const inactiveMemberCount = members.filter(m => m.status === 'Inactive').length;
    const visitorCount = visitorPagination?.total || visitors.length || 0;
    
    options.push(
      { id: 'all_members', value: 'All Members', label: `🌐 All Members (${memberCount})`, type: 'members' },
      { id: 'active_members', value: 'Active Members', label: `✅ Active Members (${activeMemberCount})`, type: 'members' },
      { id: 'inactive_members', value: 'Inactive Members', label: `⭕ Inactive Members (${inactiveMemberCount})`, type: 'members' },
      { id: 'all_visitors', value: 'All Visitors', label: `👋 All Visitors (${visitorCount})`, type: 'visitors' }
    );
    
    // 2. MEMBER GROUPS
    let groupsList = [];
    
    // Try from member pagination
    if (memberPagination?.groups && Array.isArray(memberPagination.groups)) {
      groupsList = memberPagination.groups;
    } 
    // Try from groups selector
    else if (groups && groups.length > 0) {
      groupsList = groups;
    }
    // Extract from members data
    else if (members && members.length > 0) {
      const uniqueGroups = new Set();
      members.forEach(m => {
        if (m.group) uniqueGroups.add(m.group);
      });
      groupsList = Array.from(uniqueGroups);
    }
    
    // Add member groups to options
    groupsList.forEach(group => {
      const groupMemberCount = members.filter(m => m.group === group).length;
      options.push({
        id: `group_${group}`,
        value: group,
        label: `👥 ${group} (${groupMemberCount} members)`,
        type: 'group'
      });
    });
    
    // 3. VISITOR SOURCES
    const sources = new Set();
    visitors.forEach(v => {
      if (v.source) sources.add(v.source);
      if (v.referred_by) sources.add(v.referred_by);
      if (v.visitor_type) sources.add(v.visitor_type);
    });
    
    Array.from(sources).filter(Boolean).forEach(source => {
      const sourceCount = visitors.filter(v => 
        v.source === source || v.referred_by === source || v.visitor_type === source
      ).length;
      options.push({
        id: `source_${source}`,
        value: `Source: ${source}`,
        label: `📌 ${source} (${sourceCount} visitors)`,
        type: 'visitor_source'
      });
    });
    
    // 4. BRANCHES
    const branches = new Set();
    members.forEach(m => {
      if (m.branch) branches.add(m.branch);
    });
    visitors.forEach(v => {
      if (v.branch) branches.add(v.branch);
    });
    
    Array.from(branches).filter(Boolean).forEach(branchName => {
      const memberCount = members.filter(m => m.branch === branchName).length;
      const visitorCount = visitors.filter(v => v.branch === branchName).length;
      const total = memberCount + visitorCount;
      options.push({
        id: `branch_${branchName}`,
        value: `Branch: ${branchName}`,
        label: `🏛️ ${branchName} (${total} people)`,
        type: 'branch'
      });
    });
    
    // 5. RECENT/NEW OPTIONS
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const newMembers = members.filter(m => {
      const joinedDate = new Date(m.created_at || m.joined_date);
      return joinedDate >= thirtyDaysAgo;
    });
    if (newMembers.length > 0) {
      options.push({
        id: 'new_members',
        value: 'New Members',
        label: `🌟 New Members (${newMembers.length})`,
        type: 'members'
      });
    }
    
    const recentVisitors = visitors.filter(v => {
      const visitedDate = new Date(v.visited_at || v.created_at);
      return visitedDate >= thirtyDaysAgo;
    });
    if (recentVisitors.length > 0) {
      options.push({
        id: 'recent_visitors',
        value: 'Recent Visitors',
        label: `🔔 Recent Visitors (${recentVisitors.length})`,
        type: 'visitors'
      });
    }
    
    setDynamicAudienceOptions(options);
  };

  // ==================== HELPERS ====================

  const insertField = (textareaRef, field) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    textarea.value = value.substring(0, start) + field + value.substring(end);
    textarea.focus();
    textarea.selectionStart = textarea.selectionEnd = start + field.length;
    
    const event = new Event('input', { bubbles: true });
    textarea.dispatchEvent(event);
  };

  const getDefaultDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getDefaultTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  const getAudienceLabel = (sendTo) => {
    if (!sendTo) return 'All Members';
    const option = dynamicAudienceOptions.find(opt => opt.value === sendTo);
    return option?.label || sendTo;
  };

  // Get current items for display (use events directly since API pagination handles it)
  const currentItems = events;

  // ==================== PAGINATION ====================
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== EXPORT FUNCTIONS ====================
  const handleExport = async (type, eventId = null, format = 'csv') => {
    try {
      setIsSubmitting(true);
      const params = {};
      if (eventId) params.event_id = eventId;
      
      const result = await dispatch(exportEvents({ type, params })).unwrap();
      console.log('Export result:', result);
      
      if (result.success && result.exportData) {
        let data = result.exportData;
        
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } 
        else if (typeof data === 'object' && !Array.isArray(data)) {
          if (data.data && Array.isArray(data.data)) {
            data = data.data;
          } else {
            data = Object.values(data);
          }
        }
        
        if (!Array.isArray(data) || data.length === 0) {
          showToastMessage('No data to export', 'error');
          return;
        }
        
        const filename = getExportFilename(`events-${type}`);
        
        switch (format.toLowerCase()) {
          case 'csv':
            exportToCSV(data, filename);
            break;
          case 'json':
            exportToJSON(data, filename);
            break;
          case 'excel':
            exportToExcel(data, filename);
            break;
          case 'pdf':
            exportToPDF(data, 'Events Report', filename);
            break;
          default:
            showToastMessage('Unsupported export format', 'error');
            return;
        }
        
        showToastMessage(`📥 ${data.length} records exported as ${format.toUpperCase()}!`, 'success');
        closeModal();
      }
    } catch (err) {
      console.error('Export error:', err);
      showToastMessage(err.message || 'Failed to export data', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Export Modal
  const openExportModal = (exportType = 'all', event = null) => {
    let data = [];
    let title = 'Events Report';
    let filename = 'events-report';

    if (exportType === 'single' && event) {
      const eventData = [{
        'Event Title': event.title,
        'Date': formatDateTime(event.date, null),
        'Time': formatDateTime(null, event.time),
        'Venue': event.venue,
        'Status': event.status,
        'Invites Sent': event.sent_count || 0,
        'RSVP Yes': event.rsvp_yes || 0,
        'RSVP No': event.rsvp_no || 0,
        'RSVP Rate': event.rsvp_rate || '0%',
        'Total Responses': (event.rsvp_yes || 0) + (event.rsvp_no || 0)
      }];
      data = eventData;
      title = `Event Report: ${event.title}`;
      filename = `event-${event.id}-${event.title.substring(0, 30)}`;
    } else if (exportType === 'summary') {
      const totalRsvpYes = events.reduce((sum, e) => sum + (e.rsvp_yes || 0), 0);
      const totalRsvpNo = events.reduce((sum, e) => sum + (e.rsvp_no || 0), 0);
      const totalSent = events.reduce((sum, e) => sum + (e.sent_count || 0), 0);
      
      data = events.map(e => ({
        'Event Title': e.title,
        'Date': formatDateTime(e.date, null),
        'Status': e.status,
        'Invites Sent': e.sent_count || 0,
        'RSVP Yes': e.rsvp_yes || 0,
        'RSVP No': e.rsvp_no || 0,
        'RSVP Rate': e.rsvp_rate || '0%'
      }));
      
      data.push({
        'Event Title': '=== TOTALS ===',
        'Date': '',
        'Status': '',
        'Invites Sent': totalSent,
        'RSVP Yes': totalRsvpYes,
        'RSVP No': totalRsvpNo,
        'RSVP Rate': totalSent > 0 ? `${Math.round((totalRsvpYes / totalSent) * 100)}%` : '0%'
      });
      
      title = 'Events Summary Report';
      filename = 'events-summary';
    } else {
      data = events.map(e => ({
        'Event ID': e.id,
        'Title': e.title,
        'Date': formatDateTime(e.date, null),
        'Time': formatDateTime(null, e.time),
        'Venue': e.venue,
        'Status': e.status,
        'Invites Sent': e.sent_count || 0,
        'RSVP Yes': e.rsvp_yes || 0,
        'RSVP No': e.rsvp_no || 0,
        'RSVP Rate': e.rsvp_rate || '0%',
        'Total Responses': (e.rsvp_yes || 0) + (e.rsvp_no || 0)
      }));
      title = 'All Events Report';
      filename = 'all-events';
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExport(exportType, event?.id, format)}
                  disabled={isSubmitting}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px', display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <FileSpreadsheet size={20} />}
                    {format === 'PDF' && <File size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} records will be exported</span>
          </div>

          {/* Quick Export Options */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Quick Export Options
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const summaryData = events.map(e => ({
                    'Event': e.title,
                    'Date': formatDateTime(e.date, null),
                    'RSVP Rate': e.rsvp_rate || '0%',
                    'Responses': (e.rsvp_yes || 0) + (e.rsvp_no || 0)
                  }));
                  exportToCSV(summaryData, getExportFilename('events-summary'));
                  showToastMessage(`📥 Events summary exported as CSV!`, 'success');
                  closeModal();
                }}
              >
                <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Summary Only
              </button>
              {event && (
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const rsvpData = [
                      { 'Response': 'Yes', 'Count': event.rsvp_yes || 0 },
                      { 'Response': 'No', 'Count': event.rsvp_no || 0 },
                      { 'Response': 'Pending', 'Count': (event.sent_count || 0) - (event.rsvp_yes || 0) - (event.rsvp_no || 0) }
                    ];
                    exportToCSV(rsvpData, getExportFilename(`event-${event.id}-rsvp`));
                    showToastMessage(`📥 RSVP data for "${event.title}" exported as CSV!`, 'success');
                    closeModal();
                  }}
                >
                  <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> RSVP Data Only
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAll = () => {
    if (events.length === 0) {
      showToastMessage('No events to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportEvent = (event) => {
    openExportModal('single', event);
  };

  const handleExportSummary = () => {
    if (events.length === 0) {
      showToastMessage('No events to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== EVENT CRUD ====================

  const openCreateEvent = () => {
    const defaultDate = getDefaultDate();
    const defaultTime = getDefaultTime();
    
    setTimeout(() => {
      if (createTitleRef.current) createTitleRef.current.value = '';
      if (createDateRef.current) createDateRef.current.value = defaultDate;
      if (createTimeRef.current) createTimeRef.current.value = defaultTime;
      if (createVenueRef.current) createVenueRef.current.value = '';
      if (createInviteRef.current) createInviteRef.current.value = 'All Members';
      if (createMessageRef.current) createMessageRef.current.value = '';
      if (createReminderRef.current) createReminderRef.current.value = '1 day before';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Calendar size={16} style={{ verticalAlign: '-3px' }} /> Create New Event</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              placeholder="Easter Sunday Celebration"
              ref={createTitleRef}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input 
                className="form-input" 
                type="date"
                ref={createDateRef}
                defaultValue={defaultDate}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Time</label>
              <input 
                className="form-input" 
                type="time"
                ref={createTimeRef}
                defaultValue={defaultTime}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Venue / Location</label>
            <input 
              className="form-input" 
              placeholder="Main Auditorium"
              ref={createVenueRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Invite</label>
            <select 
              className="form-select"
              ref={createInviteRef}
              defaultValue="All Members"
            >
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
              <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
              <span><BarChart3 size={14} style={{ verticalAlign: '-3px' }} /> {dynamicAudienceOptions.length} audience options</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">RSVP SMS Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={createMessageRef}
              placeholder="You're invited! 🎉 {ChurchName} presents: {EventTitle} on {Date} at {Time} — {Venue}. Reply YES to confirm or NO to decline."
            />
            <div className="form-hint">Use {'{ChurchName}'}, {'{EventTitle}'}, {'{Date}'}, {'{Time}'}, {'{Venue}'}</div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {resolvedMergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => insertField(createMessageRef, field.key)}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reminder (before event)</label>
            <select 
              className="form-select"
              ref={createReminderRef}
              defaultValue="1 day before"
            >
              <option value="1 day before">1 day before</option>
              <option value="2 days before">2 days before</option>
              <option value="Same day at 8 AM">Same day at 8 AM</option>
              <option value="No reminder">No reminder</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = createTitleRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter an event title', 'error');
                return;
              }
              
              const data = {
                title,
                date: createDateRef.current?.value || null,
                time: createTimeRef.current?.value || null,
                venue: createVenueRef.current?.value || null,
                send_to: createInviteRef.current?.value || 'All Members',
                message_template: createMessageRef.current?.value || null,
                reminder_setting: createReminderRef.current?.value || '1 day before',
                status: 'Draft',
                color: '#4f46e5'
              };
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createEvent(data)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getEvents({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getEventStats());
                  showToastMessage(`📅 "${title}" created successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to create event', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditEvent = (ev) => {
    setEditingEventId(ev.id);
    
    setTimeout(() => {
      if (editTitleRef.current) editTitleRef.current.value = ev.title || '';
      if (editDateRef.current) editDateRef.current.value = ev.date || '';
      if (editTimeRef.current) editTimeRef.current.value = ev.time || '';
      if (editVenueRef.current) editVenueRef.current.value = ev.venue || '';
      if (editInviteRef.current) editInviteRef.current.value = ev.send_to || 'All Members';
      if (editMessageRef.current) editMessageRef.current.value = ev.message_template || '';
      if (editReminderRef.current) editReminderRef.current.value = ev.reminder_setting || '1 day before';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Event: {ev.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event Title <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editTitleRef}
              defaultValue={ev.title || ''}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input 
                className="form-input" 
                type="date"
                ref={editDateRef}
                defaultValue={ev.date || ''}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Time</label>
              <input 
                className="form-input" 
                type="time"
                ref={editTimeRef}
                defaultValue={ev.time || ''}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Venue / Location</label>
            <input 
              className="form-input" 
              ref={editVenueRef}
              defaultValue={ev.venue || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Invite</label>
            <select 
              className="form-select"
              ref={editInviteRef}
              defaultValue={ev.send_to || 'All Members'}
            >
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span><Users size={14} style={{ verticalAlign: '-3px' }} /> {members.length} members loaded</span>
              <span><UserPlus size={14} style={{ verticalAlign: '-3px' }} /> {visitors.length} visitors loaded</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">RSVP SMS Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              ref={editMessageRef}
              defaultValue={ev.message_template || ''}
            />
            <div className="form-hint">Use {'{ChurchName}'}, {'{EventTitle}'}, {'{Date}'}, {'{Time}'}, {'{Venue}'}</div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {resolvedMergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => insertField(editMessageRef, field.key)}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reminder (before event)</label>
            <select 
              className="form-select"
              ref={editReminderRef}
              defaultValue={ev.reminder_setting || '1 day before'}
            >
              <option value="1 day before">1 day before</option>
              <option value="2 days before">2 days before</option>
              <option value="Same day at 8 AM">Same day at 8 AM</option>
              <option value="No reminder">No reminder</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const title = editTitleRef.current?.value?.trim() || '';
              
              if (!title) {
                showToastMessage('Please enter an event title', 'error');
                return;
              }
              
              const data = {
                title,
                date: editDateRef.current?.value || null,
                time: editTimeRef.current?.value || null,
                venue: editVenueRef.current?.value || null,
                send_to: editInviteRef.current?.value || 'All Members',
                message_template: editMessageRef.current?.value || null,
                reminder_setting: editReminderRef.current?.value || '1 day before',
              };
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateEvent({
                  id: ev.id,
                  data
                })).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getEvents({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getEventStats());
                  showToastMessage(`✅ "${title}" updated successfully!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to update event', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteEvent = (ev) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Event</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{ev.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the event and all RSVP data.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteEvent(ev.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getEvents({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getEventStats());
                  showToastMessage(`🗑️ Event "${ev.title}" deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete event', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Event
          </button>
        </div>
      </div>
    );
  };

  const handleDuplicateEvent = async (ev) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(duplicateEvent(ev.id)).unwrap();
      if (result.success) {
        await dispatch(getEvents({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getEventStats());
        showToastMessage(`📋 Event duplicated: "${result.event?.title || ev.title}"`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate event', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== SEND INVITES ====================
  const handleSendInvites = async (ev) => {
    if (ev.status !== 'Draft') {
      showToastMessage('Event is not in draft status', 'error');
      return;
    }

    const audienceLabel = getAudienceLabel(ev.send_to);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px' }} /> Send Event Invites</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--gold-pale)', border: '1px solid #f0dfa0', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}><Calendar size={14} style={{ verticalAlign: '-2px' }} /> {ev.title}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              {formatDateTime(ev.date, ev.time)} · {ev.venue}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              Audience: <strong>{audienceLabel}</strong>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Send To</label>
            <select className="form-select" defaultValue={ev.send_to || 'All Members'}>
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Invite Message</label>
            <textarea 
              className="form-textarea" 
              rows="3"
              defaultValue={ev.message_template || `You're invited to ${ev.title}! 🎉 Date: ${formatDateTime(ev.date, null)} | Time: ${formatDateTime(null, ev.time)} | Venue: ${ev.venue}. Reply YES to confirm attendance or NO to decline. God bless! — Grace Chapel`}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(sendEventInvites(ev.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getEvents({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    status: statusFilter
                  }));
                  await dispatch(getEventStats());
                  showToastMessage(`📨 Invites sent to ${result.result?.sent || 0} members!`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send invites', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send Invites'}
          </button>
        </div>
      </div>
    );
  };

  const handleSendReminder = async (ev) => {
    try {
      setIsSubmitting(true);
      const result = await dispatch(sendEventReminders(ev.id)).unwrap();
      if (result.success) {
        showToastMessage(`🔔 Reminder sent to ${result.result?.sent || 0} confirmed attendees!`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to send reminders', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkComplete = async (ev) => {
    if (ev.status === 'Completed') {
      showToastMessage('Event is already completed', 'info');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const result = await dispatch(completeEvent(ev.id)).unwrap();
      if (result.success) {
        await dispatch(getEvents({ per_page: itemsPerPage, page: currentPage }));
        await dispatch(getEventStats());
        showToastMessage(`✅ "${ev.title}" marked as completed`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to complete event', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEventDetail = (ev) => {
    // Load RSVPs for the event
    dispatch(getEventRsvps({ id: ev.id, params: { per_page: 10 } }));
    
    const audienceLabel = getAudienceLabel(ev.send_to);
    
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Calendar size={16} style={{ verticalAlign: '-3px' }} /> {ev.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            {[
              ['Date', formatDateTime(ev.date, null)],
              ['Time', formatDateTime(null, ev.time)],
              ['Venue', ev.venue],
              ['Status', ev.status],
              ['Audience', audienceLabel],
              ['RSVP Yes', ev.rsvp_yes || 0],
              ['RSVP No', ev.rsvp_no || 0],
              ['Invites Sent', ev.sent_count || 0]
            ].map(([l, v]) => (
              <div key={l} style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{l}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{v}</div>
              </div>
            ))}
          </div>
          {rsvps.length > 0 && (
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Recent RSVP Respondents</div>
              {rsvps.slice(0, 5).map(rsvp => (
                <div key={rsvp.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                  <div style={{ fontSize: '12px', flex: 1 }}>{rsvp.member?.name || rsvp.phone}</div>
                  <span className={`badge ${rsvp.response === 'yes' ? 'badge-green' : 'badge-red'}`} style={{ fontSize: '10px' }}>
                    {rsvp.response === 'yes' ? <><CheckCircle2 size={10} style={{ verticalAlign: '-1px' }} /> Going</> : <><XCircle size={10} style={{ verticalAlign: '-1px' }} /> Not Going</>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-ghost btn-sm" 
            onClick={() => handleExportEvent(ev)}
            title="Export event report"
          >
            <Download size={14} style={{ verticalAlign: '-2px' }} /> Export Report
          </button>
          {ev.status === 'Upcoming' && (
            <button
              className="btn btn-gold btn-sm"
              onClick={() => { handleSendReminder(ev); }}
              disabled={isSubmitting}
            >
              <Send size={14} style={{ verticalAlign: '-2px' }} /> Send Reminder
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { handleEditEvent(ev); closeModal(); }}
          >
            <Pencil size={14} style={{ verticalAlign: '-2px' }} /> Edit
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    totalEvents: 0,
    upcomingEvents: 0,
    completedEvents: 0,
    draftEvents: 0,
    recurringEvents: 0,
    totalRsvpYes: 0,
    totalRsvpNo: 0,
    totalSent: 0,
    avgRsvpRate: 0,
    eventsByMonth: {}
  };

  // Render Grid View
  const renderGridView = () => {
    return (
      <div className="grid-2">
        {currentItems.map(ev => {
          const statusCls = ev.status === 'Upcoming' ? 'badge-blue' : ev.status === 'Completed' ? 'badge-green' : ev.status === 'Recurring' ? 'badge-purple' : 'badge-amber';
          const rsvpPct = ev.rsvp_rate || 0;
          const audienceLabel = getAudienceLabel(ev.send_to);
          return (
            <div key={ev.id} className="card">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: `${ev.color || '#4f46e5'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Calendar size={22} /></div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>{ev.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '2px' }}>
                      {formatDateTime(ev.date, ev.time)}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                      <Users size={12} style={{ verticalAlign: '-2px' }} /> {audienceLabel}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <span className={`badge ${statusCls}`}>{ev.status}</span>
                  <button 
                    className="btn btn-ghost btn-sm btn-icon" 
                    onClick={() => handleExportEvent(ev)}
                    title={`Export ${ev.title}`}
                    disabled={isSubmitting}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleEditEvent(ev)}
                    title={`Edit ${ev.title}`}
                    disabled={isSubmitting}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDeleteEvent(ev)}
                    title={`Delete ${ev.title}`}
                    style={{ color: 'var(--red)' }}
                    disabled={isSubmitting}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--green)' }}>{ev.rsvp_yes || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Going <CheckCircle2 size={11} style={{ verticalAlign: '-2px' }} /></div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--red)' }}>{ev.rsvp_no || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Not Going <XCircle size={11} style={{ verticalAlign: '-2px' }} /></div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{ev.sent_count || 0}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Invited <Send size={11} style={{ verticalAlign: '-2px' }} /></div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: rsvpPct >= 70 ? 'var(--green)' : 'var(--amber)' }}>{rsvpPct}%</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>RSVP Rate</div>
                </div>
              </div>

              {(ev.sent_count || 0) > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>
                    <span>RSVP Progress</span>
                    <span>{ev.rsvp_yes || 0}/{ev.sent_count || 0}</span>
                  </div>
                  <div className="progress-bar"><div className="progress-fill" style={{ width: `${rsvpPct}%`, background: rsvpPct >= 70 ? 'var(--green)' : 'var(--amber)' }}></div></div>
                </div>
              )}
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '10px' }}><MapPin size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{ev.venue}</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {ev.status === 'Draft' && (
                  <button 
                    className="btn btn-gold btn-sm" 
                    onClick={() => handleSendInvites(ev)} 
                    title="Send invites for this event"
                    disabled={isSubmitting}
                  >
                    <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Invites
                  </button>
                )}
                {ev.status === 'Upcoming' && (
                  <>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleSendReminder(ev)}
                      title="Send reminder to confirmed attendees"
                      disabled={isSubmitting}
                    >
                      <Bell size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Reminder
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      style={{ background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid #a7d7be' }}
                      onClick={() => handleMarkComplete(ev)}
                      title="Mark event as completed"
                      disabled={isSubmitting}
                    >
                      <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Complete
                    </button>
                  </>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => openEventDetail(ev)}
                  title="View event details"
                  disabled={isSubmitting}
                >
                  <Eye size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Details
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleDuplicateEvent(ev)}
                  title="Duplicate this event"
                  disabled={isSubmitting}
                >
                  <Copy size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Copy
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleExportEvent(ev)}
                  title="Export event report"
                  disabled={isSubmitting}
                >
                  <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render List View
  const renderListView = () => {
    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>Time</th>
                <th>Venue</th>
                <th>Audience</th>
                <th>Status</th>
                <th>RSVP</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map(ev => {
                const statusCls = ev.status === 'Upcoming' ? 'badge-blue' : ev.status === 'Completed' ? 'badge-green' : ev.status === 'Recurring' ? 'badge-purple' : 'badge-amber';
                const rsvpPct = ev.rsvp_rate || 0;
                const audienceLabel = getAudienceLabel(ev.send_to);
                return (
                  <tr key={ev.id}>
                    <td>
                      <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{ev.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}><MapPin size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{ev.venue}</div>
                    </td>
                    <td style={{ fontSize: '12px' }}>{formatDateTime(ev.date, null)}</td>
                    <td style={{ fontSize: '12px' }}>{formatDateTime(null, ev.time)}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>{ev.venue}</td>
                    <td style={{ fontSize: '11px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {audienceLabel}
                    </td>
                    <td><span className={`badge ${statusCls}`}>{ev.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="progress-bar" style={{ width: '60px' }}>
                          <div className="progress-fill" style={{ width: `${rsvpPct}%`, background: rsvpPct >= 70 ? 'var(--green)' : 'var(--amber)' }}></div>
                        </div>
                        <span style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', color: rsvpPct >= 70 ? 'var(--green)' : 'var(--amber)' }}>
                          {rsvpPct}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon" 
                          onClick={() => openEventDetail(ev)}
                          title="View details"
                          disabled={isSubmitting}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleExportEvent(ev)}
                          title="Export"
                          disabled={isSubmitting}
                        >
                          <Download size={14} />
                        </button>
                        {ev.status === 'Draft' && (
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => handleSendInvites(ev)}
                            title="Send invites"
                            disabled={isSubmitting}
                          >
                            <Send size={14} />
                          </button>
                        )}
                        {ev.status === 'Upcoming' && (
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={() => handleSendReminder(ev)}
                            title="Send reminder"
                            disabled={isSubmitting}
                          >
                            <Bell size={14} />
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleEditEvent(ev)}
                          title="Edit"
                          disabled={isSubmitting}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDuplicateEvent(ev)}
                          title="Duplicate"
                          disabled={isSubmitting}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeleteEvent(ev)}
                          title="Delete"
                          style={{ color: 'var(--red)' }}
                          disabled={isSubmitting}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <Calendar size={16} style={{ verticalAlign: '-3px' }} /> Create events, send RSVP invites via SMS, and track attendance confirmations.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export summary of all events"
            disabled={isSubmitting}
          >
            <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Export Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all events"
            disabled={isSubmitting}
          >
            <Download size={14} style={{ verticalAlign: '-2px' }} /> Export All
          </button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={openCreateEvent} 
            title="Create a new event"
            disabled={isSubmitting}
          >
            + Create Event
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Events</div>
          <div className="stat-value">{statsData.totalEvents}</div>
          <div className="stat-change up">↑ {statsData.upcomingEvents} upcoming</div>
          <div className="stat-icon"><Calendar size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Avg RSVP Rate</div>
          <div className="stat-value">{statsData.avgRsvpRate}%</div>
          <div className="stat-change up">↑ {statsData.avgRsvpRate > 70 ? 'excellent' : 'good'}</div>
          <div className="stat-icon"><CheckCircle2 size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Total RSVPs Collected</div>
          <div className="stat-value">{statsData.totalRsvpYes + statsData.totalRsvpNo}</div>
          <div className="stat-change up">↑ all events</div>
          <div className="stat-icon"><Users size={28} strokeWidth={1.5} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">SMS Invites Sent</div>
          <div className="stat-value">{statsData.totalSent}</div>
          <div className="stat-change up">↑ this year</div>
          <div className="stat-icon"><Send size={28} strokeWidth={1.5} /></div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input 
              type="text" 
              placeholder="Search events..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search events by title or venue"
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '150px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by status"
          >
            <option value="">All Status</option>
            <option value="Upcoming">Upcoming</option>
            <option value="Completed">Completed</option>
            <option value="Draft">Draft</option>
            <option value="Recurring">Recurring</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List size={14} style={{ verticalAlign: '-2px' }} /> List
            </button>
          </div>
        </div>
      </div>

      {/* Events Content */}
      {loading && events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading events...</div>
        </div>
      ) : currentItems.length > 0 ? (
        <>
          {viewMode === 'grid' ? renderGridView() : renderListView()}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
              marginTop: '20px',
              padding: '16px',
              background: 'var(--white)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow)'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> events
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                  <select 
                    className="form-select" 
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    title="Items per page"
                    disabled={loading}
                  >
                    <option value={2}>2</option>
                    <option value={4}>4</option>
                    <option value={6}>6</option>
                    <option value={10}>10</option>
                  </select>
                </div>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToPreviousPage}
                    disabled={currentPageNum === 1 || loading}
                    style={{ 
                      opacity: currentPageNum === 1 ? 0.5 : 1, 
                      cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                    }}
                    title="Previous page"
                  >
                    ← Prev
                  </button>
                  
                  {getPageNumbers().map(page => (
                    <button 
                      key={page}
                      className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => goToPage(page)}
                      title={`Go to page ${page}`}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToNextPage}
                    disabled={currentPageNum === totalPages || loading}
                    style={{ 
                      opacity: currentPageNum === totalPages ? 0.5 : 1, 
                      cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                    }}
                    title="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon"><Calendar size={32} strokeWidth={1.5} /></div>
          <div className="empty-title">No events found</div>
          <div className="empty-text">Try adjusting your search or filters</div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 700px;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
          .event-card {
            padding: 12px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .badge-blue {
          background: var(--blue);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-green {
          background: var(--green);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-red {
          background: var(--red);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-amber {
          background: var(--amber);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-purple {
          background: #7c3aed;
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .progress-bar {
          height: 6px;
          background: var(--border);
          border-radius: 3px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.6s ease;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
      `}</style>
    </>
  );
};

export default Events;